import { useContext, useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import moment from 'moment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { AbilityContext } from '../../components/Can';
import { withLazyMounting } from '../../components/hoc/LazyDialog';
import { HallMaintenanceTask } from '../../domain/HallMaintenanceTask';
import { HallMaintenanceTaskAssignment, HallMaintenanceTaskAssignmentStatus } from '../../domain/HallMaintenanceTaskAssignment';
import { Publisher } from '../../domain/Publisher';
import { useStores } from '../../stores';
import { getLocale } from '../../utils/localeUtils';

interface Props {
    isOpen: boolean;
    task: HallMaintenanceTask;
    assignment?: HallMaintenanceTaskAssignment;
    onClose: () => void;
    onSubmit: (payload: Partial<HallMaintenanceTaskAssignment>, assignmentId?: number) => void;
}

interface FormValues {
    assignee?: Publisher;
    customAssignee?: string;
    startDate: Date | null;
    endDate?: Date | null;
    status: HallMaintenanceTaskAssignmentStatus;
    resultSummary?: string;
}

const HallMaintenanceAssignmentDialog = (props: Props) => {
    const { t } = useTranslation();
    const { publisherStore } = useStores();
    const ability = useContext(AbilityContext);
    const canManage = ability.can('manage', 'HallMaintenance');

    useEffect(() => {
        if (publisherStore.publishers.length === 0) {
            publisherStore.getAllPublishers();
        }
    }, [publisherStore]);

    const initialValues: FormValues = useMemo(() => ({
        assignee: props.assignment?.assignee,
        customAssignee: props.assignment?.customAssignee,
        startDate: props.assignment ? moment(props.assignment.startDate).toDate() : moment().toDate(),
        endDate: props.assignment?.endDate ? moment(props.assignment.endDate).toDate() : null,
        status: props.assignment?.status || HallMaintenanceTaskAssignmentStatus.NotStarted,
        resultSummary: props.assignment?.resultSummary,
    }), [props.assignment]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        onSubmit: (values) => {
            if (!canManage) {
                props.onClose();
                return;
            }

            const hasAssignee = !!values.assignee || !!values.customAssignee?.trim();
            if (!hasAssignee) {
                formik.setFieldError("assignee", t("assignment_assignee_is_required"));
                formik.setFieldError("customAssignee", t("assignment_assignee_is_required"));
                return;
            }

            if (!values.startDate) {
                formik.setFieldError("startDate", t("start_date_is_required"));
                return;
            }

            if (values.endDate && moment(values.endDate).isBefore(moment(values.startDate))) {
                formik.setFieldError("endDate", t("end_date_should_be_after_start"));
                return;
            }

            const payload: Partial<HallMaintenanceTaskAssignment> = {
                taskId: props.task.id,
                assigneeId: values.assignee?.id,
                customAssignee: values.assignee ? undefined : values.customAssignee?.trim(),
                startDate: values.startDate!,
                endDate: values.endDate || undefined,
                status: values.status as HallMaintenanceTaskAssignmentStatus,
                resultSummary: values.resultSummary || undefined,
            };

            props.onSubmit(payload, props.assignment?.id);
        },
    });

    return (
        <Dialog open={props.isOpen} fullWidth maxWidth="sm" onClose={props.onClose}>
            <form onSubmit={formik.handleSubmit}>
                <DialogTitle>
                    {props.assignment ? t("edit_assignment") : t("add_assignment")}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={0.5} sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">{t("task")}</Typography>
                        <Typography variant="subtitle1" fontWeight={700}>{props.task.title}</Typography>
                    </Stack>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Autocomplete
                                id="assignee"
                                options={publisherStore.publishers}
                                getOptionLabel={(publisher: Publisher) => publisher.firstName ? `${publisher.lastName} ${publisher.firstName}` : ''}
                                value={formik.values.assignee || null}
                                onChange={(e, publisher) => {
                                    formik.setFieldValue("assignee", publisher || undefined);
                                    if (publisher) {
                                        formik.setFieldValue("customAssignee", "");
                                    }
                                }}
                                disabled={!canManage}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        margin="normal"
                                        label={t("publisher")}
                                        helperText={formik.errors.assignee}
                                        error={!!formik.errors.assignee}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                margin="normal"
                                id="customAssignee"
                                name="customAssignee"
                                label={t("custom_assignee")}
                                type="text"
                                fullWidth
                                value={formik.values.customAssignee || ''}
                                onChange={(e) => {
                                    formik.setFieldValue("customAssignee", e.target.value);
                                    if (e.target.value) {
                                        formik.setFieldValue("assignee", undefined);
                                    }
                                }}
                                helperText={formik.errors.customAssignee || t("custom_assignee_hint")}
                                error={!!formik.errors.customAssignee}
                                InputProps={{ readOnly: !canManage }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                margin="normal"
                                id="status"
                                name="status"
                                label={t("status")}
                                fullWidth
                                value={formik.values.status}
                                onChange={formik.handleChange}
                                SelectProps={{ native: true }}
                                disabled={!canManage}
                            >
                                <option value={HallMaintenanceTaskAssignmentStatus.NotStarted}>
                                    {t("assignment_status_not_started", { defaultValue: "Not started" })}
                                </option>
                                <option value={HallMaintenanceTaskAssignmentStatus.InProgress}>
                                    {t("assignment_status_in_progress", { defaultValue: "In progress" })}
                                </option>
                                <option value={HallMaintenanceTaskAssignmentStatus.Completed}>
                                    {t("assignment_status_completed", { defaultValue: "Completed" })}
                                </option>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} locale={getLocale()}>
                                <DatePicker
                                    label={t("start_date")}
                                    value={formik.values.startDate}
                                    onChange={(value) => formik.setFieldValue("startDate", value)}
                                    disabled={!canManage}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            margin="normal"
                                            fullWidth
                                            helperText={formik.errors.startDate}
                                            error={!!formik.errors.startDate}
                                            required
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} locale={getLocale()}>
                                <DatePicker
                                    label={t("end_date")}
                                    value={formik.values.endDate}
                                    onChange={(value) => formik.setFieldValue("endDate", value)}
                                    disabled={!canManage}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            margin="normal"
                                            fullWidth
                                            helperText={formik.errors.endDate}
                                            error={!!formik.errors.endDate}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="normal"
                                id="resultSummary"
                                name="resultSummary"
                                label={t("result_summary")}
                                type="text"
                                fullWidth
                                multiline
                                minRows={3}
                                value={formik.values.resultSummary || ''}
                                onChange={formik.handleChange}
                                InputProps={{ readOnly: !canManage }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={props.onClose}>{t("cancel")}</Button>
                    {canManage && (
                        <Button color="primary" type="submit">
                            {t("submit")}
                        </Button>
                    )}
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default withLazyMounting(HallMaintenanceAssignmentDialog);
